// Mural.js
// Mural de frases: envio moderado, listagem embaralhada e curtida.
//
// Por que rola para o LADO e nao para baixo:
// a primeira versao era uma grade vertical com scroll infinito, e cada pagina
// carregada empurrava as Redes Sociais e o rodape mais para baixo. Com algumas
// centenas de frases essas secoes ficavam inalcancaveis. No trilho horizontal
// a altura da pagina e constante, tenha o mural 10 ou 30.000 frases.
//
// Como o carregamento e escalonado:
//  - nada e buscado enquanto a secao nao se aproxima da tela;
//  - a primeira leva traz so 5 frases, para aparecer conteudo quase na hora;
//  - as seguintes trazem 12, conforme a pessoa arrasta para o lado;
//  - ha um teto por sessao: como a ordem e sorteada a cada carregamento,
//    quem voltar ao site ve outras frases em vez de rolar as mesmas 30.000.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MuralContainer, SectionTitle, Subtitle, InteractiveText,
  Form, CampoWrapper, Textarea, Input, Arroba, Contador, Botao, Aviso, Restantes,
  Trilho, Card, Frase, Rodape, Handle, BotaoCurtir,
  Skeleton, Vazio, CardFinal, Total,
} from './MuralStyle';

const API = '/api';
const TEXTO_MAX = 120;
const HANDLE_MAX = 25;
const PAGINA_INICIAL = 5;   // primeira leva: pequena, para pintar rapido
const PAGINA_SEGUINTE = 12; // demais levas, ja com a pessoa arrastando
const LIMITE_SESSAO = 96;   // teto por visita; a ordem muda a cada recarga
const CHAVE_NAVEGADOR = 'mural:navegador';

/** Identificador estavel do navegador, criado uma vez e guardado localmente. */
function pegarBrowserId() {
  try {
    const salvo = window.localStorage.getItem(CHAVE_NAVEGADOR);
    if (salvo) return salvo;
    const novo = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(CHAVE_NAVEGADOR, novo);
    return novo;
  } catch {
    // localStorage bloqueado (aba anonima com restricao): funciona sem lembrar
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

const Coracao = ({ preenchido }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true"
    fill={preenchido ? 'currentColor' : 'none'}
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
  </svg>
);

export default function Mural() {
  const browserId = useMemo(pegarBrowserId, []);

  const [itens, setItens] = useState([]);
  const [semente, setSemente] = useState(null);
  const [cursor, setCursor] = useState({ k: 0, id: 0 });
  const [acabou, setAcabou] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [iniciou, setIniciou] = useState(false);
  const [falhaLista, setFalhaLista] = useState(false);
  const [total, setTotal] = useState(null);

  const [texto, setTexto] = useState('');
  const [handle, setHandle] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [batendo, setBatendo] = useState(null);

  const secaoRef = useRef(null);
  const trilhoRef = useRef(null);
  const carregandoRef = useRef(false);
  const abortRef = useRef(null);

  /* ----------------------------------------------------------- listagem */

  const carregar = useCallback(async (primeira = false) => {
    if (carregandoRef.current) return;
    if (!primeira && acabou) return;

    carregandoRef.current = true;
    setCarregando(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const params = new URLSearchParams({
        browserId,
        limite: String(primeira ? PAGINA_INICIAL : PAGINA_SEGUINTE),
      });
      if (!primeira && semente != null) {
        params.set('semente', String(semente));
        params.set('cursor', String(cursor.k));
        params.set('cursorId', String(cursor.id));
      }

      const resposta = await fetch(`${API}/frases?${params}`, { signal: controller.signal });
      if (!resposta.ok) throw new Error('falha ao listar');
      const dados = await resposta.json();

      setSemente((atual) => (atual == null ? dados.semente : atual));
      setTotal(dados.total);
      if (dados.proximoCursor != null) {
        setCursor({ k: dados.proximoCursor, id: dados.proximoCursorId });
      }

      setItens((atuais) => {
        const vistos = new Set(atuais.map((i) => i.id));
        const novos = dados.itens.filter((i) => !vistos.has(i.id));
        const juntos = primeira ? dados.itens : [...atuais, ...novos];
        // O teto por sessao entra aqui: nao adianta empilhar 30.000 cards.
        setAcabou(dados.acabou || juntos.length >= LIMITE_SESSAO);
        return juntos.slice(0, LIMITE_SESSAO);
      });
      setFalhaLista(false);
    } catch (e) {
      if (e.name !== 'AbortError') setFalhaLista(true);
    } finally {
      carregandoRef.current = false;
      setCarregando(false);
    }
  }, [acabou, browserId, cursor, semente]);

  /**
   * Primeira busca so quando a secao se aproxima da tela — a mesma ideia
   * usada no embed do Instagram. A rede de seguranca de 2,5s cobre o caso do
   * IntersectionObserver nunca disparar (viewport de altura zero, aba oculta),
   * onde o mural ficaria preso nos esqueletos para sempre.
   */
  useEffect(() => {
    const alvo = secaoRef.current;
    if (!alvo) return undefined;

    let jaPediu = false;
    let observador;
    let timer;

    const limpar = () => {
      observador?.disconnect();
      clearTimeout(timer);
    };
    const pedir = () => {
      if (jaPediu) return;
      jaPediu = true;
      setIniciou(true);
      limpar();
    };

    if (typeof IntersectionObserver === 'function') {
      observador = new IntersectionObserver((entradas) => {
        if (entradas[0]?.isIntersecting) pedir();
      }, { rootMargin: '500px' });
      observador.observe(alvo);
    } else {
      pedir();
    }

    timer = setTimeout(pedir, 2500);
    return limpar;
  }, []);

  useEffect(() => {
    if (!iniciou) return;
    carregar(true);
    fetch(`${API}/eu?browserId=${encodeURIComponent(browserId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSituacao(d))
      .catch(() => {});
    // dispara uma unica vez, quando a secao entra em cena
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iniciou]);

  /**
   * Proxima leva quando a pessoa chega perto do fim do trilho.
   *
   * Usa o evento de scroll em vez de IntersectionObserver de proposito. O IO
   * entrega callbacks no passo de renderizacao do event loop: em aba oculta ou
   * em qualquer contexto que nao esteja pintando quadros ele simplesmente nao
   * dispara, e o mural ficaria travado na primeira leva. O evento de scroll
   * nao depende disso.
   *
   * A chamada imediata cobre o caso de todos os cards carregados caberem na
   * tela sem sobra: sem barra de rolagem nao ha evento de scroll, e sem isto
   * o mural nunca passaria das 5 primeiras frases num monitor largo.
   */
  useEffect(() => {
    const raiz = trilhoRef.current;
    if (!raiz || acabou || !iniciou) return undefined;

    const aoRolar = () => {
      const restante = raiz.scrollWidth - raiz.clientWidth - raiz.scrollLeft;
      if (restante < 600) carregar(false);
    };

    raiz.addEventListener('scroll', aoRolar, { passive: true });
    aoRolar();
    return () => raiz.removeEventListener('scroll', aoRolar);
  }, [carregar, acabou, iniciou]);

  /* -------------------------------------------------------------- envio */

  const limparHandle = (valor) => valor
    .replace(/^@+/, '')
    .replace(/\s+/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._]/g, '')
    .slice(0, HANDLE_MAX);

  async function enviar(evento) {
    evento.preventDefault();
    if (enviando) return;

    setAviso(null);
    setEnviando(true);
    try {
      const resposta = await fetch(`${API}/frases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: texto.trim(), handle, browserId }),
      });
      const dados = await resposta.json().catch(() => ({}));

      if (!resposta.ok) {
        setAviso({ tipo: 'erro', texto: dados.erro || 'Não foi possível enviar agora.' });
        if (dados.bloqueado || dados.limiteAtingido) {
          setSituacao((s) => ({ ...(s || {}), bloqueado: Boolean(dados.bloqueado), restantes: 0 }));
        }
        return;
      }

      // A frase nova entra na frente do trilho para a pessoa ver o resultado.
      setItens((atuais) => [dados.frase, ...atuais].slice(0, LIMITE_SESSAO));
      setTotal((t) => (t == null ? null : t + 1));
      trilhoRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
      setTexto('');
      setHandle('');
      setAviso({ tipo: 'ok', texto: 'Frase publicada. Obrigado!' });
      setSituacao((s) => ({ ...(s || {}), restantes: dados.restantes }));
    } catch {
      setAviso({ tipo: 'erro', texto: 'Sem conexão com o servidor. Tente de novo.' });
    } finally {
      setEnviando(false);
    }
  }

  /* ------------------------------------------------------------ curtida */

  async function curtir(id) {
    setBatendo(id);
    setTimeout(() => setBatendo((atual) => (atual === id ? null : atual)), 450);

    const anterior = itens;
    setItens((atuais) => atuais.map((i) => (i.id === id
      ? { ...i, curtiu: !i.curtiu, curtidas: i.curtidas + (i.curtiu ? -1 : 1) }
      : i)));

    try {
      const resposta = await fetch(`${API}/frases/${id}/curtir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ browserId }),
      });
      if (!resposta.ok) throw new Error('recusado');
      const dados = await resposta.json();
      setItens((atuais) => atuais.map((i) => (i.id === id
        ? { ...i, curtidas: dados.curtidas, curtiu: dados.curtiu }
        : i)));
    } catch {
      setItens(anterior); // desfaz o otimismo
    }
  }

  /* ------------------------------------------------------------- render */

  const bloqueado = situacao?.bloqueado;
  const semCota = situacao?.restantes === 0;
  const podeEnviar = texto.trim().length >= 4 && handle.length >= 3 && !enviando && !bloqueado && !semCota;
  const vazio = iniciou && !carregando && itens.length === 0;
  const chegouAoTeto = acabou && itens.length >= LIMITE_SESSAO;

  return (
    <MuralContainer ref={secaoRef}>
      <SectionTitle>Mural de Frases</SectionTitle>
      <Subtitle>
        Deixe sua <span>frase</span> e assine com seu @
      </Subtitle>
      <InteractiveText>arraste para o lado e toque no coração {'>>'}</InteractiveText>

      {bloqueado ? (
        <Aviso $tipo="erro">Você não pode mais enviar frases neste mural.</Aviso>
      ) : (
        <Form onSubmit={enviar}>
          <CampoWrapper>
            <Textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, TEXTO_MAX))}
              placeholder="Escreva sua frase..."
              maxLength={TEXTO_MAX}
              disabled={enviando || semCota}
              aria-label="Sua frase"
            />
            <Contador $perto={texto.length > TEXTO_MAX - 20}>
              {texto.length}/{TEXTO_MAX}
            </Contador>
          </CampoWrapper>

          <CampoWrapper>
            <Arroba>@</Arroba>
            <Input
              value={handle}
              onChange={(e) => setHandle(limparHandle(e.target.value))}
              placeholder="seuperfil"
              maxLength={HANDLE_MAX}
              disabled={enviando || semCota}
              aria-label="Seu @"
            />
            <Contador $perto={handle.length > HANDLE_MAX - 5}>
              {handle.length}/{HANDLE_MAX}
            </Contador>
          </CampoWrapper>

          <Botao type="submit" disabled={!podeEnviar}>
            {enviando ? 'Enviando...' : 'Publicar frase'}
          </Botao>

          {aviso && <Aviso $tipo={aviso.tipo}>{aviso.texto}</Aviso>}
          {!aviso && situacao?.restantes != null && !semCota && (
            <Restantes>você ainda pode enviar {situacao.restantes} frase(s)</Restantes>
          )}
          {semCota && !aviso && <Restantes>você já usou todas as suas frases</Restantes>}
        </Form>
      )}

      {vazio && !falhaLista && (
        <Vazio>Nenhuma frase ainda. Seja a primeira pessoa a escrever.</Vazio>
      )}
      {vazio && falhaLista && (
        <Vazio>Não foi possível carregar o mural agora.</Vazio>
      )}

      {!vazio && (
        <Trilho ref={trilhoRef}>
          {itens.map((item) => (
            <Card key={item.id}>
              <Frase>{item.texto}</Frase>
              <Rodape>
                <Handle>@{item.handle}</Handle>
                <BotaoCurtir
                  type="button"
                  onClick={() => curtir(item.id)}
                  $curtiu={item.curtiu}
                  $animando={batendo === item.id}
                  aria-pressed={item.curtiu}
                  aria-label={item.curtiu ? 'Descurtir frase' : 'Curtir frase'}
                >
                  <Coracao preenchido={item.curtiu} />
                  {item.curtidas}
                </BotaoCurtir>
              </Rodape>
            </Card>
          ))}

          {(carregando || !iniciou) && Array.from(
            { length: itens.length ? 2 : PAGINA_INICIAL },
            (_, i) => <Skeleton key={`s${i}`} />,
          )}

          {chegouAoTeto && (
            <CardFinal>
              recarregue a página para ver outras frases do mural
            </CardFinal>
          )}
        </Trilho>
      )}

      {total > 0 && (
        <Total>
          {total} {total === 1 ? 'frase no mural' : 'frases no mural'}
        </Total>
      )}
    </MuralContainer>
  );
}
